<!--
  Reusable KYC Attempt Timeline. Renders the timestamped trace of a KYC attempt as a vertical
  timeline. Source is the live in-memory trace (live) or the recorded cp_event batches (kycCheckId).
  Plain English on purpose — an internal diagnostic surface for agents/developers.
-->
<mat-card class="kt" data-testid="kyc-timeline">
  <header class="kt-head" *ngIf="showHeader">
    <div class="kt-head-title">
      <mat-icon class="kt-head-icon">timeline</mat-icon>
      <div>
        <h2>KYC Attempt Timeline</h2>
        <p class="kt-head-sub">
          Everything recorded during the attempt — capture, classification, OCR, QR, pre-review,
          submission, result.
        </p>
      </div>
    </div>
    <div class="kt-head-actions">
      <button
        mat-stroked-button
        *ngIf="events.length"
        (click)="copyJson(events)"
        data-testid="kyc-timeline-copy"
      >
        <mat-icon>content_copy</mat-icon> Copy JSON
      </button>
      <button
        mat-stroked-button
        *ngIf="canRefresh"
        [color]="live ? 'warn' : 'primary'"
        (click)="refresh()"
        data-testid="kyc-timeline-refresh"
      >
        <mat-icon>{{ live ? 'clear_all' : 'refresh' }}</mat-icon> {{ live ? 'Clear' : 'Refresh' }}
      </button>
    </div>
  </header>

  <!-- Meta strip -->
  <div class="kt-meta" *ngIf="events.length">
    <span class="kt-meta-pill source" [class.live]="live">{{ live ? 'live' : 'recorded' }}</span>
    <span class="kt-meta-pill"><b>{{ events.length }}</b> events</span>
    <span class="kt-meta-pill"><b>{{ durationLabel(events) }}</b> total</span>
    <span class="kt-meta-pill attempt" *ngIf="attemptLabel">{{ attemptLabel }}</span>
    <span
      class="kt-meta-pill audit"
      *ngIf="serverAudit?.sourceIps?.length"
      [matTooltip]="'First seen ' + (serverAudit?.firstReceivedAt | date: 'medium')"
    >
      <mat-icon>lan</mat-icon> {{ serverAudit?.sourceIps?.join(', ') }}
    </span>
  </div>

  <!-- Loading -->
  <div class="kt-state" *ngIf="loading" data-testid="kyc-timeline-loading">
    <mat-progress-spinner mode="indeterminate" diameter="28"></mat-progress-spinner>
    <span>Loading timeline…</span>
  </div>

  <!-- Error -->
  <div class="kt-state error" *ngIf="error && !loading" data-testid="kyc-timeline-error">
    <mat-icon>error_outline</mat-icon><span>{{ error }}</span>
  </div>

  <!-- Empty -->
  <div class="kt-empty" *ngIf="!loading && !error && !events.length" data-testid="kyc-timeline-empty">
    <mat-icon>history_toggle_off</mat-icon>
    <p *ngIf="live">No KYC attempt traced yet. Open a KYC modal and the timeline fills in live.</p>
    <p *ngIf="!live">No recorded timeline for this check.</p>
  </div>

  <!-- Timeline -->
  <ol class="kt-list" *ngIf="!loading && !error && events.length" data-testid="kyc-timeline-list">
    <li
      class="kt-row"
      *ngFor="let e of events; trackBy: trackBySeq"
      [ngClass]="'tone-' + tone(e.level)"
      [class.has-detail]="hasDetail(e)"
      [class.open]="isExpanded(e.seq)"
      (click)="toggle(e)"
      data-testid="kyc-timeline-event"
    >
      <div class="kt-rail">
        <span class="kt-line"></span>
        <span class="kt-dot"><mat-icon>{{ meta(e.phase).icon }}</mat-icon></span>
      </div>

      <div class="kt-body">
        <div class="kt-row-top">
          <span class="kt-phase">{{ meta(e.phase).label }}</span>
          <span class="kt-title">{{ e.title }}</span>
          <span class="kt-offset">{{ offset(e.offsetMs) }}</span>
          <mat-icon class="kt-chev" *ngIf="hasDetail(e)">
            {{ isExpanded(e.seq) ? 'expand_less' : 'expand_more' }}
          </mat-icon>
        </div>

        <div class="kt-detail" *ngIf="isExpanded(e.seq)">
          <div class="kt-kv" *ngFor="let d of detailEntries(e)">
            <span class="kt-k">{{ d.k }}</span>
            <span class="kt-v">{{ d.v }}</span>
          </div>
        </div>
      </div>
    </li>
  </ol>
</mat-card>
